import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { HugeiconsIcon } from '@hugeicons/react';
import { Coins01Icon } from '@hugeicons/core-free-icons';
import { colour } from '../styles/GlobalStyles';
import '../styles/LeadsActivityRail.css';
import { useNavigate, Link } from 'react-router-dom';
import { TIER_CONFIG } from '../utils/triageLabels';
import Modal from './Modal';
import { useCompany } from '../contexts';
import { useLeadsWindow, useFilteredLeadsWindow, useProcessingLeads } from '../hooks';
import {
    deriveLeadEvents,
    resolveDisposition,
    filterReasonLabel,
    describeFilterReasons,
    safeErrorMessageLabel,
    isZoneOnlyArchive,
    DISPOSITION_CONFIG,
    DISPOSITIONS,
    SILENT_DISPOSITIONS,
    ordinalSuffix,
    resolveLeadCreditCharge
} from '../lib/leadEvents';
import { resolveLeadSourceProfile } from '../lib/leadSource';
import SourceIcon, { FaviconImg } from './SourceIcon';
import { getFaviconSrc } from '../lib/sourceFaviconUtils';
import Zillow from '../assets/icons/Zillow';
import Redfin from '../assets/icons/Redfin';
import Realtor from '../assets/icons/Realtor';
import GoogleDrive from '../assets/icons/GoogleDrive';
import OneDrive from '../assets/icons/OneDrive';
import Dropbox from '../assets/icons/Dropbox';

// The Activity Rail is a 24h log of what's landed in the pipeline.
//
// Its job is to answer one specific question: "I expected to see lead X but
// it's not in my table — what happened?" Common answer: the lead was an
// inbound copy of a property the operator already archived weeks ago, so
// the table (filtered to active, non-archived) hides it — but the rail's
// chip ("Archived" / "Duplicate" / "New sender") spells it out.
//
// Two sections:
//   1. "Processing now" — live list of in-flight leads with their stage
//      (queued → enriching → underwriting → finalizing).
//   2. "Recent events" — chronological list of every arrival in the last
//      24h, with disposition chip explaining why anomalous ones aren't in
//      the main table.
//
// Rail is read-only. Every row expands into an inline accordion showing who
// sent it, what was in the message, what we extracted, and what happened.
// Processed leads show a "View lead →" link in their expanded section; filtered
// leads don't (no lead doc was created). Mutations stay in the table / lead
// detail to preserve the "table = work, rail = log" split.
//
// Open/closed state persists in localStorage so the operator's preference
// sticks across reloads. Default open ≥1280px viewport, closed below.


const STORAGE_KEY = 'leads-activity-rail-open';
// 24-hour window — matches the rail's "why isn't this lead in my table"
// use case (operators wonder about leads they just got, not leads from
// last week). Window picker was removed because operators rarely changed
// it; expanding the window adds noise without answering the actual
// question.
const WINDOW_DAYS = 1;
const WINDOW_LABEL = '24 hours';
// How many feed rows to render before the "Show more" affordance. Reveal is
// client-side only (slices the already-fetched window) — see the reveal state
// in LeadsActivityRail for why server pagination is the wrong tool here.
const REVEAL_PAGE = 15;

// Processing-stage chip configs. Stage values are written by the worker — see
// functions/propertyIngestionV2/batchedFirestore.js updateLeadProcessingStage.
// Legacy in-flight leads without a stage value render as "Processing".
const STAGE_LABELS = {
    queued:               { label: 'Queued',       chipClass: 'bg-colour-light' },
    enriching:            { label: 'Enriching',    chipClass: 'bg-colour-blue-01' },
    running_underwriting: { label: 'Underwriting', chipClass: 'bg-colour-purple-01' },
    finalizing:           { label: 'Finalizing',   chipClass: 'bg-colour-jade-01' }
};

function getInitialOpen() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === '0') return false;
        if (stored === '1') return true;
    } catch (e) {
        // localStorage unavailable (private window, etc.) — fall through to default
    }
    return typeof window !== 'undefined' && window.innerWidth >= 1280;
}

function LeadsActivityRail({ open, onToggle }) {
    const { companyId, loading: companyLoading } = useCompany();
    const navigate = useNavigate();

    // Tick every 30s to keep "33m ago" labels fresh without refetching.
    const [nowTick, setNowTick] = useState(() => Date.now());
    useEffect(() => {
        if (!open) return undefined;
        const id = setInterval(() => setNowTick(Date.now()), 30_000);
        return () => clearInterval(id);
    }, [open]);

    const sinceMs = WINDOW_DAYS * 24 * 60 * 60 * 1000;

    // Subscription stays paused while collapsed to avoid Firestore reads the
    // user can't see. The rail itself stays mounted so the width can animate
    // — we just delay the data fetch until it's actually visible.
    const pausedCompanyId = (!open || companyLoading) ? null : companyId;
    const { leads, loading, refresh: refreshLeads } = useLeadsWindow(pausedCompanyId, sinceMs);
    // Filtered-out arrivals (dropped during ingestion). Separate fetch because
    // they're archived and the main window query excludes archived leads.
    const { leads: filteredLeads, loading: filteredLoading } = useFilteredLeadsWindow(
        pausedCompanyId,
        sinceMs
    );

    // Real-time subscription — onSnapshot so new leads appear immediately
    // rather than waiting up to 30s for the next poll.
    const { leads: processingLeads, loading: processingLoading } = useProcessingLeads(pausedCompanyId);

    // When a lead finishes processing, the onSnapshot fires immediately and
    // removes it from processingLeads. Without this, the Recent Events feed
    // would still show the lead as "Processing" for up to 30s (stale poll).
    // Kick a refresh so both sections reflect the completed state together.
    //
    // Track by identity (leadId set) not count, so a simultaneous complete+enqueue
    // (count stays the same) still triggers the refresh for the completed lead.
    const prevProcessingIdsRef = useRef(null);
    useEffect(() => {
        const curr = new Set(processingLeads.map((l) => l.leadId));
        const prev = prevProcessingIdsRef.current;
        if (prev !== null) {
            const anyCompleted = [...prev].some((id) => !curr.has(id));
            if (anyCompleted) refreshLeads();
        }
        prevProcessingIdsRef.current = curr;
    }, [processingLeads, refreshLeads]);

    const visibleEvents = useMemo(() => {
        // Compute cutoff inline so nowTick ticks don't re-derive the full event
        // list — the leads/filteredLeads arrays themselves change on every 30s
        // poll, which is the correct trigger. Time labels refresh via nowTick
        // being passed into row components directly.
        const cutoff = Date.now() - sinceMs;
        const out = [];
        for (const lead of leads) {
            const events = deriveLeadEvents(lead, cutoff);
            for (const e of events) {
                e.disposition = resolveDisposition(e);
                out.push(e);
            }
        }
        // Filtered-out leads aren't dedup-event streams — emit one synthetic
        // event each, carrying the explicit FILTERED disposition so FilteredRow
        // renders the reason as the row label (most have no address).
        for (const lead of filteredLeads) {
            const data = lead?.data;
            if (!data) continue;
            const ts = toMillis(data.createdAt);
            if (!ts || ts < cutoff) continue;
            out.push({
                type: DISPOSITIONS.FILTERED,
                disposition: DISPOSITIONS.FILTERED,
                timestamp: ts,
                leadId: lead.leadId,
                propertyId: lead.propertyId || data.propertyId || null,
                lead,
                filterReasons: data.filterReasons || null,
                errorMessage: data.errorMessage || null,
                source: data.source || data.originSource || null
            });
        }
        out.sort((a, b) => b.timestamp - a.timestamp);
        return out;
    }, [leads, filteredLeads, sinceMs]);

    // Split the feed into two groups: real arrivals (Recent events) and
    // ingestion-dropped leads (Filtered out). visibleEvents is already sorted
    // desc, so the filtered subsets preserve that order.
    const recentEvents = useMemo(
        () => visibleEvents.filter(e => e.disposition !== DISPOSITIONS.FILTERED),
        [visibleEvents]
    );
    const filteredEvents = useMemo(
        () => visibleEvents.filter(e => e.disposition === DISPOSITIONS.FILTERED),
        [visibleEvents]
    );

    // Feed view toggle — default to processed arrivals; the operator can swap
    // to the filtered-out view. Falls back to processed when there's nothing
    // filtered, so the toggle never strands the user on an empty view.
    const [feedTab, setFeedTab] = useState('processed');
    const showFilteredTab = filteredEvents.length > 0;
    const activeTab = (feedTab === 'filtered' && showFilteredTab) ? 'filtered' : 'processed';
    const activeEvents = activeTab === 'filtered' ? filteredEvents : recentEvents;
    // Progressive reveal — render a capped slice of the (already-fetched)
    // events and let the user reveal more. Server pagination would be wasteful
    // here: getOrderedDocs paginates in-memory, so a deeper page costs a full
    // re-scan. We already hold the whole 24h window client-side, so revealing
    // more is a free slice. Per-tab so switching doesn't carry the count over.
    const [shownProcessed, setShownProcessed] = useState(REVEAL_PAGE);
    const [shownFiltered, setShownFiltered] = useState(REVEAL_PAGE);
    const shown = activeTab === 'filtered' ? shownFiltered : shownProcessed;
    const setShown = activeTab === 'filtered' ? setShownFiltered : setShownProcessed;
    const visibleActiveEvents = useMemo(() => activeEvents.slice(0, shown), [activeEvents, shown]);
    const remainingCount = activeEvents.length - visibleActiveEvents.length;
    const [modalEvent, setModalEvent] = useState(null);
    const selectFeedTab = useCallback((tab) => {
        setFeedTab(tab);
        setModalEvent(null);
        if (tab === 'filtered') setShownFiltered(REVEAL_PAGE);
        else setShownProcessed(REVEAL_PAGE);
    }, []);

    const handleProcessingRowClick = useCallback((propertyId) => {
        if (!propertyId) return;
        navigate(`/leads/${propertyId}`, { state: { leadsSearch: window.location.search } });
    }, [navigate]);

    // Single rail element — width animates between expanded + collapsed via
    // CSS transition. Content stays in the DOM throughout so React doesn't
    // tear down + rebuild the subtree mid-animation (which would flash and
    // also cancel any inner transitions). Hidden via opacity + visibility +
    // pointer-events when collapsed; the inner container is also taken out
    // of the layout flow so the collapsed rail can shrink to its 32px strip.
    return (
        <>
        {modalEvent ? (
            <LeadDetailModal event={modalEvent} companyId={companyId} onClose={() => setModalEvent(null)} />
        ) : null}
        <aside
            className={`leads-activity-rail ${open ? 'leads-activity-rail-open' : 'leads-activity-rail-collapsed'}`}
            aria-label="Activity rail"
        >
            <button
                type="button"
                className="leads-activity-rail-collapsed-toggle"
                onClick={onToggle}
                aria-label="Open activity rail"
                aria-expanded={open}
                title="Open activity rail"
                tabIndex={open ? -1 : 0}
                aria-hidden={open}
            >
                ⟨
            </button>

            <div className="leads-activity-rail-inner" aria-hidden={!open}>
                <div className="leads-activity-rail-header">
                    <span className="heading-small-bold colour-primary">Activity</span>
                    <button
                        type="button"
                        className="leads-activity-rail-toggle"
                        onClick={onToggle}
                        aria-label="Collapse activity rail"
                        aria-expanded={open}
                        title="Collapse"
                        tabIndex={open ? 0 : -1}
                    >
                        ⟩
                    </button>
                </div>

                <ProcessingNowSection
                    leads={processingLeads}
                    onRowClick={handleProcessingRowClick}
                    nowTick={nowTick}
                    loading={processingLoading}
                />

                <div className="leads-activity-rail-divider" />

                <div className="leads-activity-rail-feed-header">
                    {showFilteredTab ? (
                        <div className="leads-activity-window" role="tablist" aria-label="Activity feed">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'processed'}
                                className={`leads-activity-window-option xs-label ${activeTab === 'processed' ? 'leads-activity-window-option-active xs-label-bold' : ''}`}
                                onClick={() => selectFeedTab('processed')}
                            >
                                Processed
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'filtered'}
                                className={`leads-activity-window-option xs-label ${activeTab === 'filtered' ? 'leads-activity-window-option-active xs-label-bold' : ''}`}
                                onClick={() => selectFeedTab('filtered')}
                            >
                                {`Filtered out · ${filteredEvents.length}`}
                            </button>
                        </div>
                    ) : (
                        <span className="label-bold colour-primary">Recent events</span>
                    )}
                </div>

                {(loading || filteredLoading) ? (
                    <FeedSkeleton />
                ) : activeEvents.length === 0 ? (
                    <EmptyFeed
                        message={showFilteredTab
                            ? `No deals in the last ${WINDOW_LABEL} — everything was filtered out.`
                            : `No events in the last ${WINDOW_LABEL}.`}
                    />
                ) : (
                    <ul className="leads-activity-rail-feed">
                        {visibleActiveEvents.map((event, idx) => {
                            const rowId = `${event.leadId || event.propertyId || 'x'}-${event.timestamp}-${idx}`;
                            if (activeTab === 'filtered') {
                                return (
                                    <FilteredRow
                                        key={rowId}
                                        event={event}
                                        nowTick={nowTick}
                                        onOpenModal={() => setModalEvent(event)}
                                    />
                                );
                            }
                            return (
                                <EventRow
                                    key={rowId}
                                    event={event}
                                    nowTick={nowTick}
                                    onOpenModal={() => setModalEvent(event)}
                                />
                            );
                        })}
                        {remainingCount > 0 ? (
                            <li>
                                <button
                                    type="button"
                                    className="leads-activity-rail-show-more body-semibold colour-secondary"
                                    onClick={() => setShown((s) => s + REVEAL_PAGE)}
                                >
                                    {`Show ${Math.min(REVEAL_PAGE, remainingCount)} more`}
                                </button>
                            </li>
                        ) : null}
                    </ul>
                )}
            </div>
        </aside>
        </>
    );
}

function ProcessingNowSection({ leads, onRowClick, nowTick, loading }) {
    return (
        <div className="leads-activity-rail-processing">
            <div className="leads-activity-rail-section-header">
                <span className="label-bold colour-primary">Processing now</span>
                <span className="xs-label colour-secondary">
                    {leads.length}
                </span>
            </div>
            {loading && leads.length === 0 ? (
                <div className="leads-activity-rail-processing-skel" aria-busy="true" />
            ) : leads.length === 0 ? (
                <div className="leads-activity-rail-processing-empty">
                    <span className="body-regular colour-secondary">
                        No leads in flight.
                    </span>
                </div>
            ) : (
                <ul className="leads-activity-rail-processing-list">
                    {leads.map((lead) => (
                        <ProcessingRow
                            key={lead.leadId || lead.propertyId}
                            lead={lead}
                            onClick={onRowClick}
                            nowTick={nowTick}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}

function ProcessingRow({ lead, onClick, nowTick }) {
    const data = lead?.data || {};
    const address = data.address || {};
    const streetAddress = address.streetAddress || 'Unknown address';
    const stage = data.processingStage || null;
    const stageCfg = stage ? STAGE_LABELS[stage] : null;
    // createdAt → "Xm ago" so operators see how long the lead's been queued.
    const createdMs = data.createdAt?.toMillis?.() || data.createdAt?.seconds * 1000 || null;
    const ageLabel = createdMs ? formatRelativeTime(createdMs, nowTick) : '';

    return (
        <li>
            <button
                type="button"
                className="leads-activity-rail-processing-row"
                onClick={() => onClick(lead.propertyId || lead.leadId)}
            >
                <div className="leads-activity-rail-processing-row-top">
                    <span className="body-semibold colour-primary leads-activity-rail-processing-row-address">
                        {streetAddress}
                    </span>
                    {ageLabel ? (
                        <span className="xs-label colour-secondary">{ageLabel}</span>
                    ) : null}
                </div>
                <div className="leads-activity-rail-processing-row-bottom">
                    <span
                        className={`leads-activity-rail-stage-chip xs-label-bold colour-primary ${stageCfg?.chipClass || 'bg-colour-light'}`}
                    >
                        {stageCfg?.label || 'Processing'}
                    </span>
                </div>
            </button>
        </li>
    );
}

function eventResultLabel(event) {
    const data = event.lead?.data || {};
    const d = event.disposition;
    if (d === DISPOSITIONS.DUPLICATE) return 'Duplicate of an existing lead';
    if (d === DISPOSITIONS.RE_BLAST) {
        const ord = event.senderOrdinal;
        return ord ? `${ord}${ordinalSuffix(ord)} unique sender on this property` : 'New sender on existing lead';
    }
    if (d === DISPOSITIONS.PRICE_CHANGE) return 'Updated asking price on existing lead';
    if (d === DISPOSITIONS.ARCHIVED) return 'Lead is archived';
    if (d === DISPOSITIONS.RE_SURFACED) return 'Previously archived lead re-surfaced';
    if (d === DISPOSITIONS.DISCARDED) return safeErrorMessageLabel(data.errorMessage) || 'No address found — lead was not created';
    // HOT / PROCESSED — show triage + ARV if available
    const tier = data.triage?.priority_tier;
    const tierLabel = tier ? (TIER_CONFIG[tier]?.label || null) : null;
    const arv = data.underwriting?.arv;
    const arvStr = arv ? `$${Number(arv).toLocaleString()} est. value` : null;
    if (tierLabel && arvStr) return `${tierLabel} · ${arvStr}`;
    if (tierLabel) return tierLabel;
    if (arvStr) return arvStr;
    return 'Processed';
}

function EventRow({ event, nowTick, onOpenModal }) {
    const cfg = DISPOSITION_CONFIG[event.disposition] || DISPOSITION_CONFIG.processing;
    const data = event.lead?.data || {};
    const address = data.address || {};
    const streetAddress = address.streetAddress || 'Unknown address';
    const sourceProfile = resolveLeadSourceProfile({ event });
    const sourceToEmail = data.sourceMetadata?.toEmail || null;
    const sourceTooltip = sourceToEmail
        ? `${sourceProfile.label} · received at ${sourceToEmail}`
        : sourceProfile.label;
    const chipLabel = (event.disposition === DISPOSITIONS.RE_BLAST && event.senderOrdinal)
        ? `${event.senderOrdinal}${ordinalSuffix(event.senderOrdinal)} sender`
        : cfg.label;
    const isSilent = SILENT_DISPOSITIONS.has(event.disposition);

    return (
        <li>
            <button
                type="button"
                className={`leads-activity-rail-event-row ${isSilent ? 'leads-activity-rail-event-row-silent' : ''}`}
                onClick={onOpenModal}
            >
                <span className="leads-activity-rail-event-time xs-label colour-secondary">
                    {formatRelativeTime(event.timestamp, nowTick)}
                </span>
                {isSilent ? (
                    <span aria-hidden="true" className="leads-activity-rail-event-chip-spacer" />
                ) : (
                    <span className={`leads-activity-rail-event-chip xs-label-bold colour-primary ${cfg.chipClass}`}>
                        {cfg.emoji ? <span aria-hidden="true">{cfg.emoji}</span> : null}
                        <span>{chipLabel}</span>
                    </span>
                )}
                <span className={`body-semibold leads-activity-rail-event-address ${isSilent ? 'colour-secondary' : 'colour-primary'}`}>
                    {streetAddress}
                </span>
                <span className="leads-activity-rail-event-source" title={sourceTooltip}>
                    <SourceIcon profile={sourceProfile} size={14} />
                </span>
            </button>
        </li>
    );
}

// Plain-language "where in the pipeline did this get dropped".
const FILTER_STAGE_LABELS = {
    pre_extraction:  'before reading',
    pre_enrichment:  'while reading',
    post_enrichment: 'after analysis'
};

// Best human label for who sent this lead, across all drop shapes and pipeline
// stages. currentSender is the enriched contact (processed leads); originalSender
// is the pre-enrichment contact (filtered leads); partialStructuredData.sender
// is the raw extracted name before any enrichment.
function filteredSender(data) {
    const sm = data.sourceMetadata || {};
    const contact = data.currentSender || data.originalSender || {};
    const name = sm.senderName || data.partialStructuredData?.sender || contact.name || null;
    const email = sm.senderEmail || contact.email || null;
    if (name && email) return `${name} · ${email}`;
    return name || email || 'Unknown sender';
}

// Primary identity for a filtered row's first line — the most specific thing
// we know. Address (post-enrichment rejects) wins; otherwise who sent it. This
// is what makes two same-reason rows ("No address found") distinguishable.
function filteredIdentity(data) {
    return data.address?.streetAddress
        || data.sourceMetadata?.senderName
        || data.partialStructuredData?.sender
        || data.sourceMetadata?.senderEmail
        || data.originalSender?.name
        || 'Unknown sender';
}

function hostnameOf(url) {
    try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

// Only render links we can prove are http(s). dealUrls are scraped from inbound
// emails (attacker-influenceable), so a `javascript:`/`data:` href would be an
// XSS vector when clicked — rel="noopener" does not guard against scheme abuse.
function isSafeHttpUrl(url) {
    try {
        const proto = new URL(url).protocol;
        return proto === 'http:' || proto === 'https:';
    } catch { return false; }
}

const LINK_ICON_MAP = [
    { match: /(^|\.)zillow\.com$/,                                            Icon: Zillow },
    { match: /(^|\.)redfin\.com$/,                                            Icon: Redfin },
    { match: /(^|\.)realtor\.com$/,                                           Icon: Realtor },
    { match: /(^|\.)drive\.google\.com$|(^|\.)docs\.google\.com$|(^|\.)sheets\.google\.com$/, Icon: GoogleDrive },
    { match: /(^|\.)onedrive\.live\.com$|(^|\.)1drv\.ms$/,                   Icon: OneDrive },
    { match: /(^|\.)dropbox\.com$/,                                           Icon: Dropbox },
];

// sourceFavicons is optional — when absent, unknown-host links get no icon.
function linkIcon(url, sourceFavicons) {
    try {
        const host = new URL(url).hostname;
        const knownEntry = LINK_ICON_MAP.find(({ match }) => match.test(host));
        if (knownEntry) return <knownEntry.Icon className="leads-activity-rail-link-icon" />;
        const strippedHost = host.toLowerCase().replace(/^www\./, '');
        const faviconSrc = getFaviconSrc(strippedHost, sourceFavicons);
        if (faviconSrc) {
            return <FaviconImg src={faviconSrc} alt="" size={14} className="leads-activity-rail-link-icon" />;
        }
        return null;
    } catch { return null; }
}

// Links propertyIngestionV2 pulled from the message + the listing pages it
// matched the property to (sourceUrls). Deduped, labelled, http(s) only.
//
// Source of truth is data.urlActivity (the per-link lifecycle ledger): every
// link the lead carried, resolved to its destination, with what happened to it.
// We prefer it because dealUrls is pruned downstream — junk is dropped and
// cloud-storage links (e.g. the Drive photo folder) are promoted OUT into
// imageFolders, so dealUrls alone no longer reflects everything we scraped.
// Junk dispositions (skipped social/home/unsubscribe) are excluded — they
// weren't scraped. Falls back to dealUrls for older leads with no ledger.
function collectLinks(data) {
    const out = [];
    const seen = new Set();
    const push = (url, label) => {
        if (!url || typeof url !== 'string' || seen.has(url) || !isSafeHttpUrl(url)) return;
        seen.add(url);
        out.push({ url, label: label || hostnameOf(url) });
    };
    const ledger = Array.isArray(data.urlActivity?.links) ? data.urlActivity.links : null;
    if (ledger && ledger.length) {
        for (const l of ledger) {
            if (l && typeof l.disposition === 'string' && l.disposition.startsWith('skipped')) continue;
            push(l?.url || l?.originalUrl);
        }
    } else {
        (Array.isArray(data.dealUrls) ? data.dealUrls : []).forEach((u) => push(u));
        // no_address filtered leads keep their links under partialStructuredData.
        (Array.isArray(data.partialStructuredData?.dealUrls) ? data.partialStructuredData.dealUrls : []).forEach((u) => push(u));
    }
    const su = data.sourceUrls || {};
    push(su.zillow, 'Zillow');
    push(su.redfin, 'Redfin');
    push(su.realtor, 'Realtor');
    return out;
}

function DetailLine({ label, value }) {
    return (
        <div className="leads-activity-rail-detail-line">
            <span className="xs-label-bold colour-secondary leads-activity-rail-detail-label">{label}</span>
            <span className="xs-label colour-primary leads-activity-rail-detail-value">{value}</span>
        </div>
    );
}

// Per-row credit marker for the filtered tab. Answers the question the removed
// blanket "these didn't cost a credit" note used to answer WRONGLY: a filtered
// lead may or may not have been paid for, depending on whether its gate fired
// before or after the billing point — and on Liam's book most of them were paid
// for. Both states are rendered rather than only the charged one, so a bare row
// can never be misread as "we haven't checked".
//
// The charge is read off the lead's own ledger (resolveLeadCreditCharge), never
// inferred from the filter reason — see that helper for why the reason code
// cannot be trusted to decide this.
function FilteredCreditBadge({ charge }) {
    if (charge.state === 'charged') {
        return (
            <span
                className="leads-activity-rail-credit-badge leads-activity-rail-credit-badge-charged"
                title={charge.label}
                // role="img" so the label below is actually announced — aria-label on a
                // bare span (role=generic) is not reliably exposed. The visible content
                // is a coin glyph plus a naked numeral, which would read as "2".
                role="img"
                aria-label={`Cost ${charge.credits} credit${charge.credits === 1 ? '' : 's'}`}
            >
                <HugeiconsIcon icon={Coins01Icon} color={colour.gray06} size={12} aria-hidden="true" />
                <span className="xs-label-bold colour-primary" aria-hidden="true">{charge.credits}</span>
            </span>
        );
    }
    // Both remaining states cost nothing, but they are different events and the
    // operator reads them differently — see resolveLeadCreditCharge.
    const refunded = charge.state === 'refunded';
    return (
        <span
            className="leads-activity-rail-credit-badge leads-activity-rail-credit-badge-uncharged"
            title={charge.label}
            role="img"
            aria-label={refunded ? 'Charged, then refunded' : 'Never charged'}
        >
            <span className="xs-label colour-secondary" aria-hidden="true">
                {refunded ? 'Refunded' : 'Free'}
            </span>
        </span>
    );
}

function FilteredRow({ event, nowTick, onOpenModal }) {
    const data = useMemo(() => event.lead?.data || {}, [event.lead?.data]);
    const sourceProfile = resolveLeadSourceProfile({ event });
    const identity = filteredIdentity(data);
    const inbox = data.sourceMetadata?.toEmail || null;
    const timeAgo = formatRelativeTime(event.timestamp, nowTick);
    const sourceTooltip = inbox ? `${sourceProfile.label} · received at ${inbox}` : sourceProfile.label;

    // Per-row expansion — local state because each row is independent and the
    // user may want to keep several open at once while scanning the filtered
    // tab. No localStorage; expansion is a transient comparison aid, not a
    // preference worth persisting.
    const [expanded, setExpanded] = useState(false);
    const reasonItems = useMemo(
        () => describeFilterReasons(event.filterReasons, data.filterDetails, data),
        [event.filterReasons, data]
    );
    // Headline is the summary shown when collapsed. Prefer the curated error
    // message (e.g. "Address was incomplete") when present — it's already a
    // single, complete sentence. Otherwise use the reasons summary.
    const errorHeadline = safeErrorMessageLabel(event.errorMessage);
    const headline = errorHeadline || filterReasonLabel(event.filterReasons);
    const hasDetail = reasonItems.length > 0;
    const charge = useMemo(() => resolveLeadCreditCharge(data), [data]);

    const handleToggle = useCallback(() => {
        if (!hasDetail) {
            // Nothing to expand — fall back to opening the full-message modal
            // so the row still does something useful when clicked.
            onOpenModal();
            return;
        }
        setExpanded((prev) => !prev);
    }, [hasDetail, onOpenModal]);

    return (
        <li>
            <button
                type="button"
                className={`leads-activity-rail-filtered-row ${expanded ? 'leads-activity-rail-filtered-row-expanded' : ''}`}
                onClick={handleToggle}
                aria-expanded={hasDetail ? expanded : undefined}
            >
                <span className="leads-activity-rail-filtered-main">
                    <span className="body-semibold colour-primary leads-activity-rail-filtered-identity">
                        {identity}
                    </span>
                    <span className="xs-label colour-secondary leads-activity-rail-filtered-sub">
                        {headline} · {timeAgo}
                    </span>
                </span>
                <FilteredCreditBadge charge={charge} />
                {hasDetail ? (
                    <span
                        className={`leads-activity-rail-filtered-caret ${expanded ? 'leads-activity-rail-filtered-caret-open' : ''}`}
                        aria-hidden="true"
                    >
                        ›
                    </span>
                ) : (
                    <span className="leads-activity-rail-event-source" title={sourceTooltip}>
                        <SourceIcon profile={sourceProfile} size={14} />
                    </span>
                )}
            </button>
            {expanded && hasDetail ? (
                <FilteredReasonsAccordion
                    reasonItems={reasonItems}
                    onOpenModal={onOpenModal}
                />
            ) : null}
        </li>
    );
}

// Modal-side render of the reason list. Same data shape as the rail accordion,
// styled to sit within the modal's meta block (no toggle, no "view message"
// affordance — the modal is already the full-detail view).
function FilteredReasonsModalSection({ reasonItems, stageLabel }) {
    return (
        <div className="leads-activity-rail-detail-line leads-activity-modal-reasons">
            <span className="xs-label-bold colour-secondary leads-activity-rail-detail-label">
                {reasonItems.length > 1 ? 'Reasons' : 'Reason'}
            </span>
            <div className="leads-activity-rail-detail-value">
                <ul className="leads-activity-rail-reason-list">
                    {reasonItems.map((r) => (
                        <li key={r.code} className="leads-activity-rail-reason-item">
                            <span className="xs-label-bold colour-primary leads-activity-rail-reason-title">
                                {r.title}
                            </span>
                            {r.body ? (
                                <span className="xs-label colour-secondary leads-activity-rail-reason-body">
                                    {r.body}
                                </span>
                            ) : null}
                        </li>
                    ))}
                </ul>
                {stageLabel ? (
                    <span className="xs-label colour-secondary leads-activity-rail-reason-stage">
                        Filtered {stageLabel}.
                    </span>
                ) : null}
            </div>
        </div>
    );
}

function FilteredReasonsAccordion({ reasonItems, onOpenModal }) {
    return (
        <div className="leads-activity-rail-filtered-accordion">
            <ul className="leads-activity-rail-reason-list">
                {reasonItems.map((r) => (
                    <li key={r.code} className="leads-activity-rail-reason-item">
                        <span className="xs-label-bold colour-primary leads-activity-rail-reason-title">
                            {r.title}
                        </span>
                        {r.body ? (
                            <span className="xs-label colour-secondary leads-activity-rail-reason-body">
                                {r.body}
                            </span>
                        ) : null}
                    </li>
                ))}
            </ul>
            <button
                type="button"
                className="leads-activity-rail-reason-more xs-label-bold colour-secondary"
                onClick={onOpenModal}
            >
                View full message →
            </button>
        </div>
    );
}

// Build readable key/value rows for the "what we extracted" pane, tolerating
// every drop shape (partialStructuredData, or fields hydrated onto the lead).
function buildStructuredRows(structured, data) {
    const s = structured || {};
    const rows = [];
    const sender = s.sender || data.sourceMetadata?.senderName
        || data.currentSender?.name || data.originalSender?.name;
    if (sender) rows.push({ label: 'Sender', value: sender });
    const price = s.price ?? data.currentAskingPrice;
    if (price != null) rows.push({ label: 'Price', value: `$${Number(price).toLocaleString()}` });
    const propertyType = s.propertyType || data.propertyType;
    if (propertyType) rows.push({ label: 'Type', value: propertyType });
    const addr = s.address || data.address;
    if (addr && (addr.streetAddress || addr.city || addr.state || addr.zipcode)) {
        const parts = [addr.streetAddress, addr.city, [addr.state, addr.zipcode].filter(Boolean).join(' ')].filter(Boolean);
        rows.push({ label: 'Address', value: parts.join(', ') });
    }
    if (s.notes) rows.push({ label: 'Notes', value: s.notes });
    return rows;
}

function LeadDetailModal({ event, companyId, onClose }) {
    const data = event.lead?.data || {};
    const isFiltered = event.disposition === DISPOSITIONS.FILTERED;
    const cfg = DISPOSITION_CONFIG[event.disposition];
    const title = filteredIdentity(data);
    const sender = filteredSender(data);
    const inbox = data.sourceMetadata?.toEmail || null;
    const subject = data.sourceMetadata?.subject || null;
    const stageLabel = FILTER_STAGE_LABELS[data.filterStage] || null;
    const reasonItems = isFiltered
        ? describeFilterReasons(event.filterReasons, data.filterDetails, data)
        : [];
    const fallbackReason = isFiltered
        ? (safeErrorMessageLabel(event.errorMessage) || filterReasonLabel(event.filterReasons))
        : eventResultLabel(event);
    const fallbackWithStage = (isFiltered && stageLabel) ? `${fallbackReason} (${stageLabel})` : fallbackReason;
    // A target-geography archive is currently the only filtered disposition this rail knows has a
    // fully-underwritten, viewable lead behind it (functions/followUp/uncardedLeads.js#archiveOutOfGeographyLeads
    // archives it only after underwriting and buyer matching landed) — every OTHER filter reason today means
    // underwriting never ran, so there is nothing to view. If a future archive reason joins that class, teach
    // isZoneOnlyArchive (src/lib/leadEvents.js) about it there, not here — this line just asks the shared answer.
    const isViewableFiltered = isFiltered && isZoneOnlyArchive({ filterReasons: event.filterReasons, filterStage: data.filterStage });
    const canViewLead = (!isFiltered || isViewableFiltered) && !!event.propertyId;
    // Spell out in words what the row's coin badge says in a glyph. Filtered rows
    // only — a processed lead's credits are the normal cost of doing business, so
    // calling them out here would just be noise.
    const charge = isFiltered ? resolveLeadCreditCharge(data) : null;

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const subheadingComponent = (
        <div className="leads-activity-modal-subheading">
            {cfg && !isFiltered ? (
                <span className={`leads-activity-rail-stage-chip xs-label-bold colour-primary ${cfg.chipClass}`}>
                    {cfg.emoji ? <span aria-hidden="true">{cfg.emoji} </span> : null}{cfg.label}
                </span>
            ) : null}
            <span className="xs-label colour-secondary">{formatRelativeTime(event.timestamp, Date.now())}</span>
        </div>
    );

    const body = (
        <div className="leads-activity-modal-body-inner">
            <div className="leads-activity-modal-meta">
                <DetailLine label="From" value={sender} />
                {inbox ? <DetailLine label="To" value={inbox} /> : null}
                {subject ? <DetailLine label="Subject" value={subject} /> : null}
                {isFiltered && reasonItems.length > 0 ? (
                    <FilteredReasonsModalSection
                        reasonItems={reasonItems}
                        stageLabel={stageLabel}
                    />
                ) : (
                    <DetailLine label={isFiltered ? 'Reason' : 'Result'} value={fallbackWithStage} />
                )}
                {charge ? <DetailLine label="Cost" value={charge.label} /> : null}
            </div>
            <LeadInlineDetail lead={event.lead} companyId={companyId} />
            {canViewLead ? (
                <Link
                    to={`/leads/${event.propertyId}`}
                    state={{ leadsSearch: window.location.search }}
                    className="leads-activity-rail-detail-link body-semibold no-underline"
                    onClick={onClose}
                >
                    View lead →
                </Link>
            ) : null}
        </div>
    );

    return createPortal(
        <div className="leads-activity-modal-backdrop" onClick={onClose}>
            <div className="leads-activity-modal-wrap" onClick={(e) => e.stopPropagation()}>
                <Modal
                    heading={title}
                    subheading={null}
                    subheadingComponent={subheadingComponent}
                    body={body}
                    type="none"
                    cancelText={null}
                    cancelButtonCTA={null}
                    actionButtonText={null}
                    actionButtonCTA={null}
                    secondaryButtonText={null}
                    secondaryButtonCTA={null}
                    exitButtonCTA={onClose}
                    loading={false}
                    actionButtonDisabled={false}
                    leftIcon={null}
                    rightIcon={null}
                    cancelLeftIcon={null}
                    cancelRightIcon={null}
                    customClass={null}
                    promptInput={false}
                    prompt=""
                    onChangePrompt={null}
                    submitPrompt={null}
                    setTextAreaHeight={null}
                />
            </div>
        </div>,
        document.body
    );
}

// Lazy-fetches the raw message body + extracted fields for a lead, then renders
// them in the detail modal. rawData/messageBody aren't in the rail projection,
// so a single getDoc fires on first mount; the result is cached in component
// state for the lifetime of the open modal.
function LeadInlineDetail({ lead, companyId }) {
    const data = lead?.data || {};
    const leadId = lead?.leadId || data.leadId || null;
    const [rawBody, setRawBody] = useState(data.messageBody || data.rawData || null);
    const [structured, setStructured] = useState(data.partialStructuredData || null);
    const [links, setLinks] = useState(() => collectLinks(data));
    // sourceFavicons from the full Firestore doc (not in the rail's projection).
    // Populated on first expand — used by linkIcon + label for brand identity.
    const [sourceFavicons, setSourceFavicons] = useState(data.sourceFavicons || null);
    const [loading, setLoading] = useState(false);
    // Prevents re-fetching on parent re-renders (e.g. the 30s getOrderedDocs poll
    // delivers a new lead object reference with the same content).
    const fetchedRef = useRef(false);

    useEffect(() => {
        if (!companyId || !leadId || String(leadId).startsWith('mock_') || fetchedRef.current) return undefined;
        fetchedRef.current = true;
        let cancelled = false;
        setLoading(true);
        getDoc(doc(getFirestore(), 'companies', companyId, 'leads', leadId))
            .then((snap) => {
                if (cancelled) return;
                const d = snap.exists() ? snap.data()?.data : null;
                if (d) {
                    setRawBody(d.messageBody || d.rawData || null);
                    setStructured((prev) => prev || d.partialStructuredData || null);
                    setLinks((prev) => (prev.length ? prev : collectLinks(d)));
                    if (d.sourceFavicons) setSourceFavicons(d.sourceFavicons);
                }
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [leadId, companyId]);

    const structuredRows = buildStructuredRows(structured, data);

    if (loading) return null;
    if (!rawBody && structuredRows.length === 0 && links.length === 0) return null;

    return (
        <div className="leads-activity-rail-inline-detail">
            {rawBody ? (
                <div className="leads-activity-rail-inline-section">
                    <span className="xs-label-bold colour-secondary">Raw message</span>
                    <pre className="leads-activity-rail-raw xs-label colour-primary">{rawBody}</pre>
                </div>
            ) : null}
            {structuredRows.length > 0 ? (
                <div className="leads-activity-rail-inline-section">
                    <span className="xs-label-bold colour-secondary">What we extracted</span>
                    <div className="leads-activity-rail-struct">
                        {structuredRows.map((r) => (
                            <DetailLine key={r.label} label={r.label} value={r.value} />
                        ))}
                    </div>
                </div>
            ) : null}
            {links.length > 0 ? (
                <div className="leads-activity-rail-inline-section">
                    <span className="xs-label-bold colour-secondary">Links found in message</span>
                    <div className="leads-activity-rail-links-list">
                        {links.map((l) => {
                            // Prefer a scraped brand name (e.g. "FasterDeals") over the
                            // raw hostname for unknown hosts. Known sources (Zillow etc.)
                            // already carry an explicit label from collectLinks.
                            const host = hostnameOf(l.url);
                            const brandName = sourceFavicons?.[host]?.name || null;
                            const displayLabel = brandName || l.label;
                            return (
                                <a
                                    key={l.url}
                                    href={l.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="xs-label leads-activity-rail-link"
                                    title={l.url}
                                >
                                    {linkIcon(l.url, sourceFavicons)}
                                    {displayLabel}
                                </a>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function FeedSkeleton() {
    return (
        <ul className="leads-activity-rail-feed" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
                <li key={i}>
                    <div className="leads-activity-rail-event-row leads-activity-rail-event-row-skeleton" />
                </li>
            ))}
        </ul>
    );
}

function EmptyFeed({ message }) {
    return (
        <div className="leads-activity-rail-empty">
            <span className="body-regular colour-secondary text-align-center">
                {message}
            </span>
        </div>
    );
}

// Normalize a Firestore timestamp to epoch ms. getOrderedDocs returns
// JSON-serialized timestamps ({_seconds,_nanoseconds}); SDK reads give
// {seconds} or a Timestamp with toMillis(). Handle all three.
function toMillis(ts) {
    if (!ts) return 0;
    if (typeof ts.toMillis === 'function') return ts.toMillis();
    if (ts._seconds !== undefined) return ts._seconds * 1000;
    if (ts.seconds !== undefined) return ts.seconds * 1000;
    if (typeof ts === 'number') return ts;
    return 0;
}

function formatRelativeTime(ms, refMs) {
    if (!ms) return '';
    const diff = (refMs || Date.now()) - ms;
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
}

// Export a hook that owns open/closed state + persistence — keeps the
// localStorage logic and the toggle handler colocated near the component
// rather than scattered in Leads.js. Caller wires `open` + `onToggle`.
export function useActivityRailState() {
    const [open, setOpen] = useState(getInitialOpen);
    const onToggle = useCallback(() => {
        setOpen((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
            } catch (e) { /* ignore */ }
            return next;
        });
    }, []);
    return { open, onToggle };
}

export default LeadsActivityRail;
